import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, PenLine } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Fallback placeholders for early days when no approved community
// testimonials exist yet. Clearly attributed to a first name + city so
// they read as real early-reader whispers.
const FALLBACK = [
  { body: "The first app to make me feel less mad on solar storm days.", name: "Sara", city: "Brighton" },
  { body: "My birth chart poster is on my wall. I read it every morning.", name: "Priya", city: "Toronto" },
  { body: "I finally have a name for what my body's been doing for 30 years.", name: "Marco", city: "Lisbon" },
  { body: "The Sunday letter is the calmest inbox arrival of my week.", name: "Ada", city: "Manchester" },
  { body: "Compatibility reading called us out kindly. We laughed and cried.", name: "Jules + Rob", city: "Bristol" },
  { body: "I open the guidance before the news now. My nervous system thanks me.", name: "Nadia", city: "Amsterdam" },
];

/**
 * Horizontal marquee of real community testimonials, falling back to
 * placeholders when the approved pool is smaller than 3. Users can leave
 * their own via the pen-line CTA under the strip.
 */
export default function QuoteMarquee() {
  const [quotes, setQuotes] = useState(FALLBACK);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND}/api/testimonials?limit=24`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((d) => {
        if (cancelled) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        if (items.length >= 3) {
          setQuotes(items.map((t) => ({ body: t.body, name: t.name, city: t.city })));
          setIsLive(true);
        }
      })
      .catch(() => { /* keep placeholders */ });
    return () => { cancelled = true; };
  }, []);

  // Duplicate for seamless left-scrolling loop
  const items = [...quotes, ...quotes];

  return (
    <section
      className="relative border-t border-b border-[color:var(--gold)]/10"
      style={{ background: "linear-gradient(180deg, rgba(212,162,76,0.02), transparent 60%, rgba(212,162,76,0.02))" }}
      data-testid="quote-marquee-section"
      aria-label="What sensitive people say"
    >
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 pt-10 md:pt-14 pb-10 md:pb-14">
        <div className="text-center label-overline mb-6 md:mb-8">
          <Sparkles size={12} className="inline -mt-0.5 mr-2 text-[color:var(--gold)]" />
          Whispers from your guidance
        </div>

        <div
          className="quote-marquee-viewport"
          role="marquee"
          aria-live="off"
          data-testid="quote-marquee"
        >
          <div className="quote-marquee-track">
            {items.map((q, i) => (
              <figure
                key={i}
                className="quote-chip"
                data-testid={`quote-chip-${i % quotes.length}`}
              >
                <blockquote className="font-serif-italic text-[color:var(--parchment)] text-base md:text-lg leading-snug">
                  &ldquo;{q.body}&rdquo;
                </blockquote>
                <figcaption className="mt-3 label-overline text-[10px] text-[color:var(--gold)]/90">
                  — {q.name}{q.city ? `, ${q.city}` : ""}
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="quote-marquee-fade quote-marquee-fade-l" aria-hidden="true" />
          <div className="quote-marquee-fade quote-marquee-fade-r" aria-hidden="true" />
        </div>

        <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          <p className="font-serif-italic text-[color:var(--text-muted)] text-xs">
            {isLive
              ? "Whispers from the community. Yours can rotate in too."
              : "Names are placeholders for early readers. Real whispers rotate in as they arrive."}
          </p>
          <Link
            to="/testimonial"
            className="inline-flex items-center gap-2 label-overline text-[10px] text-[color:var(--gold)] hover:text-[color:var(--gold-soft)] transition"
            data-testid="quote-marquee-cta"
          >
            <PenLine size={11} /> Leave a whisper
          </Link>
        </div>
      </div>
    </section>
  );
}
